import { AnalyticsEvent, type TrackEventInput } from '@cibli/shared';
import { useCallback } from 'react';

import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useTrackingContext } from '@/features/common/contexts/tracking.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { useBooth } from '@/features/settings/hooks/useBooth';

import { trackEvent } from './track-event';

/**
 * Hook pour tracker des événements
 * Fournit automatiquement le contexte (eventSessionId, platformType, userId, boothId, interviewId)
 */
export function useTrackEvent() {
  const { interview } = useInterviewContext();
  const { user } = useUserContext();
  const { eventSessionId } = useTrackingContext();
  const { platformType, booth } = useBooth();

  const enrichEventParams = useCallback(
    (params: TrackEventInput): TrackEventInput => {
      const needsInterviewId =
        params.event === AnalyticsEvent.STARTED_INTERVIEW ||
        params.event === AnalyticsEvent.ENDED_INTERVIEW ||
        params.event === AnalyticsEvent.DOWNLOADED_CV ||
        params.event === AnalyticsEvent.PRINTED_CV;

      if (needsInterviewId && interview?.id) {
        return {
          ...params,
          props: {
            ...params.props,
            interviewId: interview.id,
          },
        } as TrackEventInput;
      }

      return params;
    },
    [interview?.id],
  );

  const track = useCallback(
    (params: TrackEventInput) => {
      const enrichedParams = enrichEventParams(params);

      return trackEvent(enrichedParams as TrackEventInput, {
        eventSessionId,
        platformType,
        userId: user?.id,
        boothId: booth?.id,
      });
    },
    [enrichEventParams, eventSessionId, platformType, user?.id, booth?.id],
  );

  return track;
}
